import { Link, useNavigate } from "react-router-dom";
import { Phone, MessageSquare, Menu, X, Instagram, Facebook } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { telHref, smsHref } from "@/lib/api";

export function Header({ settings }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const phone = settings?.phone || "";
  const links = [
    { label: "Services", href: "#services" },
    { label: "Pricing", href: "#pricing" },
    { label: "Gallery", href: "#gallery" },
    { label: "Reviews", href: "#reviews" },
    { label: "Visit Us", href: "#contact" },
  ];
  return (
    <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <span className="w-9 h-9 grid place-items-center bg-primary text-primary-foreground rounded-md font-head font-black text-sm">WM</span>
          <span className="font-head font-extrabold tracking-tight uppercase text-sm sm:text-base text-slate-900">Window Tint</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="text-xs uppercase tracking-[0.15em] text-slate-500 hover:text-primary transition-colors">{l.label}</a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <a href={telHref(phone)} data-testid="header-call-btn" className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-primary transition-colors">
            <Phone className="w-4 h-4" /> {phone}
          </a>
          <Button data-testid="header-book-btn" onClick={() => navigate("/book")} className="rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider text-xs">Book Now</Button>
        </div>
        <button data-testid="mobile-menu-toggle" className="md:hidden text-slate-900" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-slate-200 px-5 py-4 flex flex-col gap-4">
          {links.map((l) => <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-sm uppercase tracking-wider text-slate-600">{l.label}</a>)}
          <div className="flex gap-3 pt-2">
            <a href={telHref(phone)} className="flex-1"><Button variant="outline" className="w-full rounded-md border-slate-300"><Phone className="w-4 h-4 mr-2" />Call</Button></a>
            <a href={smsHref(settings?.sms_number || phone)} className="flex-1"><Button variant="outline" className="w-full rounded-md border-slate-300"><MessageSquare className="w-4 h-4 mr-2" />Text</Button></a>
          </div>
          <Button onClick={() => { setOpen(false); navigate("/book"); }} className="rounded-md bg-primary text-primary-foreground font-bold uppercase">Book Appointment</Button>
        </div>
      )}
    </header>
  );
}

function GoogleIcon({ className }) {
  return (<svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 11v2.8h4.6c-.2 1.2-1.4 3.5-4.6 3.5A5.3 5.3 0 1 1 12 6.7c1.5 0 2.5.6 3.1 1.2l2.1-2C15.9 4.7 14.2 4 12 4a8 8 0 1 0 0 16c4.6 0 7.6-3.2 7.6-7.7 0-.5 0-.9-.1-1.3H12z"/></svg>);
}
function TikTokIcon({ className }) {
  return (<svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.5 3c.3 2 1.5 3.6 3.5 3.9v2.4c-1.2 0-2.4-.3-3.5-.9v6.2a5.6 5.6 0 1 1-5.6-5.6c.3 0 .6 0 .9.1v2.5a3.1 3.1 0 1 0 2.2 3V3h2.5z"/></svg>);
}

function socialHref(key, val) {
  const v = (val || "").trim();
  if (!v) return "#";
  if (v.startsWith("http")) return v;
  const handle = v.replace(/^@/, "");
  if (key === "instagram") return `https://instagram.com/${handle}`;
  if (key === "tiktok") return `https://tiktok.com/@${handle}`;
  if (key === "facebook") return `https://facebook.com/${handle}`;
  return v;
}

export function SocialLinks({ settings, className = "" }) {
  const sl = settings?.social_links || {};
  const items = [
    { key: "google", label: "Google", Icon: GoogleIcon },
    { key: "instagram", label: "Instagram", Icon: Instagram },
    { key: "tiktok", label: "TikTok", Icon: TikTokIcon },
    { key: "facebook", label: "Facebook", Icon: Facebook },
  ].filter((i) => (sl[i.key] || "").trim());
  if (items.length === 0) return null;
  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="footer-socials">
      {items.map(({ key, label, Icon }) => (
        <a key={key} href={socialHref(key, sl[key])} target="_blank" rel="noreferrer"
          aria-label={label} data-testid={`social-${key}`}
          className="w-9 h-9 grid place-items-center rounded-md bg-white/10 text-slate-200 hover:bg-primary hover:text-primary-foreground transition-colors">
          <Icon className="w-4 h-4" />
        </a>
      ))}
    </div>
  );
}

export function Footer({ settings }) {
  return (
    <footer className="bg-slate-900 text-slate-300 py-12 px-5 sm:px-8">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-9 h-9 grid place-items-center bg-primary text-primary-foreground rounded-md font-head font-black text-sm">WM</span>
            <span className="font-head font-extrabold tracking-tight uppercase text-white">Window Tint</span>
          </div>
          <p className="text-sm text-slate-400 max-w-xs">{settings?.tagline}</p>
          <SocialLinks settings={settings} className="mt-5" />
        </div>
        <div className="text-sm space-y-1">
          <p className="text-white font-semibold uppercase text-xs tracking-[0.2em] mb-2">Contact</p>
          <p>{settings?.address}</p>
          <p><a href={telHref(settings?.phone)} className="hover:text-primary">{settings?.phone}</a></p>
          <p>{settings?.email}</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between text-xs text-slate-400">
        <p>© {new Date().getFullYear()} WM Window Tint. Fresno, CA.</p>
        <Link to="/admin/login" data-testid="admin-link" className="hover:text-primary">Admin</Link>
      </div>
    </footer>
  );
}
